<template>
  <footer class="footer px-4 container-fluid">
    <div class="container">
      <div class="footer-content">
        <div class="brand-section">
          <img
            src="@/assets/images/logo.webp"
            alt="Semillero del Oeste"
            class="footer-logo"
            width="46"
            height="65"
            decoding="async"
            loading="lazy"
          >
          <div class="text-section">
            <p class="copyright mb-0">
              © {{ currentYear }} Semillero del oeste
            </p>
          </div>
        </div>

        <div class="social-links">
          <a
            href="https://instagram.com/semillerodeloeste"
            target="_blank"
            rel="noopener noreferrer"
            class="social-link"
            aria-label="Semillero del Oeste en Instagram (se abre en una pestaña nueva)"
          >
            <svg class="social-svg" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
              <path
                fill="currentColor"
                d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8A3.6 3.6 0 0 0 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6C20 4 18 2 15.2 2h-7.6M12 6.5A5.5 5.5 0 1 1 6.5 12 5.5 5.5 0 0 1 12 6.5m0 2A3.5 3.5 0 1 0 15.5 12 3.5 3.5 0 0 0 12 8.5m5.5-3.5a1 1 0 1 1-1 1 1 1 0 0 1 1-1z"
              />
            </svg>
          </a>
          <a
            href="https://www.facebook.com/profile.php?id=61568482152973"
            target="_blank"
            rel="noopener noreferrer"
            class="social-link"
            aria-label="Semillero del Oeste en Facebook (se abre en una pestaña nueva)"
          >
            <svg class="social-svg" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
              <path
                fill="currentColor"
                d="M12 2C6.5 2 2 6.5 2 12c0 5 3.7 9.1 8.4 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.2c-1.2 0-1.6.7-1.6 1.5V12h2.8l-.4 2.9h-2.3v7C18.3 21.1 22 17 22 12c0-5.5-4.5-10-10-10z"
              />
            </svg>
          </a>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { computed } from 'vue';

const currentYear = computed(() => new Date().getFullYear());
</script>

<style scoped>
/* Aislar del Poppins global: evita CLS cuando entran las fuentes de Google */
.footer,
.footer * {
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
}

.footer {
  padding: 2rem 0;
  position: relative;
  overflow: hidden;
  contain: layout style;
  min-height: 8.5rem;
  line-height: 1.45;
  text-rendering: optimizeLegibility;
}

.footer-content {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-radius: 16px;
  min-height: 6.25rem;
}

.brand-section {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  min-height: 68px;
}

.footer-logo {
  height: 65px;
  width: 46px;
  object-fit: contain;
  flex-shrink: 0;
  opacity: 0.9;
}

.text-section {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-height: 5.25rem;
  justify-content: center;
}

.copyright {
  color: var(--white-color-2);
  font-size: 0.9rem !important;
  font-weight: 400 !important;
  line-height: 1.4 !important;
  min-height: 1.35rem;
}

.credits {
  opacity: 0.7;
  transition: opacity 0.3s ease;
  min-height: 3rem;
}

.dev-badge-img {
  max-width: 140px;
  margin-top: 0.35rem;
  display: block;
  height: auto;
}

.credits:hover {
  opacity: 1;
}

.developed-by {
  color: var(--white-color-2);
  font-size: 0.8rem !important;
  font-weight: 400 !important;
  line-height: 1.35 !important;
}

.dev-link {
  color: var(--primary-color);
  text-decoration: none;
  transition: color 0.2s ease, text-decoration 0.2s ease;
  font-weight: 500 !important;
  font-size: 0.8rem !important;
}

.dev-link:hover {
  color: var(--white-color);
  text-decoration: underline;
}

.social-links {
  display: flex;
  gap: 0.75rem;
  flex-shrink: 0;
}

.social-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  color: var(--white-color-2);
  text-decoration: none;
  box-sizing: border-box;
  /* sin transform en hover: menos reflow/CLS en móvil */
  transition: background-color 0.2s ease, color 0.2s ease;
}

.social-link:hover {
  background: var(--primary-color);
  color: white;
}

.social-svg {
  display: block;
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .footer {
    min-height: 15rem;
  }

  .footer-content {
    flex-direction: column;
    gap: 1.5rem;
    text-align: center;
    padding: 1.25rem;
    min-height: 13rem;
  }

  .brand-section {
    flex-direction: column;
    gap: 1rem;
    min-height: 9rem;
  }

  .text-section {
    align-items: center;
    min-height: 5.75rem;
  }

  .social-links {
    width: 100%;
    justify-content: center;
  }
}
</style>
